import { Injectable } from '@angular/core';
import { BoxxImageModel } from '../models/BoxxImage.model';
import { BoxxImageUtils } from '../utils/imageUtils';
import { BoxxImagePickerTemplateMethod } from './ImagePickerTemplateMethod';

@Injectable()
export class BoxxImagePickerPWAPicker extends BoxxImagePickerTemplateMethod {
  constructor(private boxxImageUtils: BoxxImageUtils) {
    super();
  }

  setPickerOptions(options: BoxxWebBrowserPickerOptions) {
    this.pickerOptions = options;
    return this;
  }

  async getSourceImages(): Promise<BoxxImageModel[]> {
    const result = await this.pickImagesFromBrowser(
      this.pickerOptions as BoxxWebBrowserPickerOptions
    );

    return new Promise((resolve) => {
      const promises = result.map(async (imgObj) => {
        if (imgObj.data.dimensions.width > this.imageConstraints.maxWidth) {
          const resizedHeight =
            (100 * imgObj.data.dimensions.height) /
            imgObj.data.dimensions.width;

          const resizedImage = await this.boxxImageUtils.resizeBase64Image(
            imgObj.data.content,
            {
              width: this.imageConstraints.maxWidth,
              height: resizedHeight,
              outputFormat: 'jpeg',
            }
          );

          const bytes = await this.boxxImageUtils.getImgFileSize(resizedImage);
          const dimensions = await this.boxxImageUtils.getImgBase64Dimensions(
            resizedImage
          );

          const resizedBoxxImage = new BoxxImageModel({
            bytes,
            dimensions,
            content: resizedImage,
            path: null,
          });

          return resizedBoxxImage;
        }

        return new BoxxImageModel({
          bytes: imgObj.data.size,
          dimensions: imgObj.data.dimensions,
          content: imgObj.data.content,
          path: null,
        });
      });

      resolve(Promise.all(promises));
    });
  }

  private pickImagesFromBrowser(options?: BoxxWebBrowserPickerOptions): Promise<
    | {
        status: 'success' | 'error';
        data: IBrowserImageDataProps;
      }[]
    | undefined
  > {
    const pickerOptions = {
      type: 'file',
      accept: 'image/*',
      multiple: true,
      ...options,
    };

    const input = document.createElement('input');
    input.type = pickerOptions.type;
    input.accept = pickerOptions.accept;
    input.multiple = pickerOptions.multiple;

    return new Promise((resolve) => {
      const promises: Promise<{
        status: 'success' | 'error';
        data: IBrowserImageDataProps | any;
      }>[] = [];

      input.onchange = (e: any) => {   
        const files = e.target.files;
        Object.keys(files).forEach((key) => {
          const reader = new FileReader();
          const file = files[key];
          reader.readAsDataURL(file);

          promises.push(
            new Promise((response) => {
              reader.onload = async (readerEvent) => {
                const content = readerEvent.target.result as string;

                const normalizedImg: IBrowserImageDataProps = {
                  lastModified: file.lastModified,
                  lastModifiedDate: file.lastModifiedDate,
                  name: file.name,
                  size: file.size,
                  type: file.type,
                  dimensions: await this.boxxImageUtils.getImgDimensions(
                    content
                  ),
                  content,
                  file,
                };
                return response({ status: 'success', data: normalizedImg });
              };

              reader.onerror = async (readerEvent) =>
                response({ status: 'error', data: readerEvent.target.error });
            })
          );
        });

        return Promise.all(promises).then((resp) => {
          // remove the event listener since we dont need it anymore, to cleanup resources
          window.removeEventListener('focus', onCancelListener);
          window.removeEventListener('touchend', onCancelListener);
          resolve(Promise.all(promises));
          return resp;
        });
      };

      const onCancelListener = async (e) => {   
          setTimeout(() => {
            if(input.files.length === 0){
              window.removeEventListener('focus', onCancelListener);
              window.removeEventListener('touchend', onCancelListener);
              resolve(Promise.all(promises));
            }
          }, 350); // wait a beat, so that if onchange is firing simultaneously, it takes precedent
      };

      window.addEventListener('focus', onCancelListener, { once: true });
      window.addEventListener('touchend', onCancelListener, { once: true });

      input.click();
    });
  }
}

export interface IBrowserImageDataProps {
  lastModified: number;
  lastModifiedDate: Date;
  name: string;
  size: number;
  type: string;
  dimensions: { width: number; height: number };
  content: string;
  file?: File;
}

export type BoxxWebBrowserPickerOptions = {
  type?: string; //e.g. 'file'
  accept?: string; //e.g. 'image/jpg'
  multiple?: boolean;
};
